import { useEffect, type RefObject } from 'react';

/**
 * IntersectionObserver-backed infinite-scroll trigger hook.
 *
 * Currently single-consumer in `LibraryRoute` (Batch 8 Commit 4) —
 * placed in `pwa/src/hooks/` per the existing precedent
 * (`useEntitlement` etc.) so a future 2nd consumer (likely
 * Notification-Center post-launch when notification list grows
 * beyond default 50) finds it at the expected location.
 *
 * Behavior:
 *   - Observes `triggerRef.current` with a 200px rootMargin so the
 *     fetch fires BEFORE the trigger element is fully visible
 *     (avoids visible "jump-and-load").
 *   - Calls `onIntersect` once per intersection event. Caller is
 *     responsible for guarding against re-entrant fetches via the
 *     `enabled` flag (e.g. `enabled={!loadingMore && hasMore}`).
 *   - When `enabled` flips to false, the observer is disconnected
 *     and re-armed when it flips back to true.
 *
 * Tracker candidate (post-launch): if a 2nd consumer surfaces
 * with a different rootMargin or threshold need, lift the
 * IntersectionObserver options to a 4th argument with sensible
 * defaults. Until then the inline 200px is fine.
 */
export function useInfiniteScroll(
  triggerRef: RefObject<HTMLElement | null>,
  onIntersect: () => void,
  enabled: boolean,
): void {
  useEffect(() => {
    if (!enabled) return;
    const node = triggerRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) onIntersect();
      },
      { rootMargin: '200px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled, onIntersect, triggerRef]);
}
